/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Find the nearest point on a route polyline to the user's location
 * @param {Object} userLocation - User's current location {latitude, longitude}
 * @param {Array} polyline - Array of route coordinates [{lat, lng}]
 * @returns {Object} {nearestPoint, distanceToRoute, segmentIndex, progressOnRoute}
 */
export const findNearestPointOnRoute = (userLocation, polyline) => {
  if (!userLocation || !polyline || polyline?.length === 0) {
    return { nearestPoint: null, distanceToRoute: Infinity, segmentIndex: 0, progressOnRoute: 0 };
  }

  let minDistance = Infinity;
  let nearestPoint = null;
  let nearestSegmentIndex = 0;
  let progressOnRoute = 0;

  for (let i = 0; i < polyline?.length - 1; i++) {
    const p1 = polyline?.[i];
    const p2 = polyline?.[i + 1];
    const result = findNearestPointOnSegment(
      userLocation?.latitude,
      userLocation?.longitude,
      p1?.lat,
      p1?.lng,
      p2?.lat,
      p2?.lng
    );

    if (result?.distance < minDistance) {
      minDistance = result?.distance;
      nearestPoint = result?.point;
      nearestSegmentIndex = i;
      progressOnRoute = (i + result?.t) / (polyline?.length - 1);
    }
  }

  return {
    nearestPoint,
    distanceToRoute: minDistance,
    segmentIndex: nearestSegmentIndex,
    progressOnRoute
  };
};

/**
 * Find nearest point on a line segment
 * @param {number} px - User latitude
 * @param {number} py - User longitude
 * @param {number} x1 - Segment start latitude
 * @param {number} y1 - Segment start longitude
 * @param {number} x2 - Segment end latitude
 * @param {number} y2 - Segment end longitude
 * @returns {Object} {point, distance, t}
 */
const findNearestPointOnSegment = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;

  let t = 0;
  if (lengthSquared !== 0) {
    t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
  }

  const nearestLat = x1 + t * dx;
  const nearestLng = y1 + t * dy;
  const distance = calculateDistance(px, py, nearestLat, nearestLng);

  return {
    point: { lat: nearestLat, lng: nearestLng },
    distance,
    t
  };
};

/**
 * Calculate remaining distance along the route from a given point
 * @param {Array} polyline - Route polyline coordinates
 * @param {number} segmentIndex - Index of the segment where user is nearest
 * @param {number} progressOnRoute - Progress along the route (0-1)
 * @returns {number} Remaining distance in meters
 */
export const calculateRemainingDistance = (polyline, segmentIndex, progressOnRoute) => {
  if (!polyline || polyline?.length === 0) return 0;

  let remainingDistance = 0;

  // Calculate distance from current position to end of current segment
  if (segmentIndex < polyline?.length - 1) {
    const currentSegmentStart = polyline?.[segmentIndex];
    const currentSegmentEnd = polyline?.[segmentIndex + 1];
    const segmentLength = calculateDistance(
      currentSegmentStart?.lat,
      currentSegmentStart?.lng,
      currentSegmentEnd?.lat,
      currentSegmentEnd?.lng
    );
    const segmentProgress = progressOnRoute * (polyline?.length - 1) - segmentIndex;
    remainingDistance += segmentLength * (1 - segmentProgress);
  }

  // Add remaining segments
  for (let i = segmentIndex + 1; i < polyline?.length - 1; i++) {
    const p1 = polyline?.[i];
    const p2 = polyline?.[i + 1];
    remainingDistance += calculateDistance(p1?.lat, p1?.lng, p2?.lat, p2?.lng);
  }

  return remainingDistance;
};

/**
 * Check if current time is in peak hour
 * @param {Date} currentTime - Current time
 * @returns {boolean} True if peak hour
 */
export const isPeakHour = (currentTime = new Date()) => {
  const hour = currentTime?.getHours();
  const minute = currentTime?.getMinutes();
  const timeInMinutes = hour * 60 + minute;

  const morningPeakStart = 7 * 60 + 30; // 07:30
  const morningPeakEnd = 9 * 60 + 30; // 09:30
  const eveningPeakStart = 17 * 60; // 17:00
  const eveningPeakEnd = 19 * 60; // 19:00

  return (
    (timeInMinutes >= morningPeakStart && timeInMinutes <= morningPeakEnd) ||
    (timeInMinutes >= eveningPeakStart && timeInMinutes <= eveningPeakEnd)
  );
};

/**
 * Calculate ETA based on user location, route geometry, and departure times
 * Enhanced with Gemini AI analysis of historical delay patterns
 * @param {Object} params - Calculation parameters
 * @param {Object} params.userLocation - User location {latitude, longitude}
 * @param {Array} params.routePolyline - Route geometry [{lat, lng}]
 * @param {string} params.departureTime - Next departure time (HH:MM)
 * @param {number} params.routeDurationMinutes - Total route duration
 * @param {Date} params.currentTime - Current time
 * @param {string} params.routeId - Route ID (optional, for AI enhancement)
 * @param {boolean} params.useAI - Whether to use AI enhancement (default: true)
 * @returns {Object} {etaMinutes, confidenceInterval, status, aiPrediction}
 */
export const calculateETAWithLocation = async ({
  userLocation,
  routePolyline,
  departureTime,
  routeDurationMinutes,
  currentTime = new Date(),
  routeId = null,
  useAI = true
}) => {
  if (!userLocation || !routePolyline || !departureTime) {
    return { etaMinutes: 0, confidenceInterval: 5, status: 'unknown' };
  }

  // Parse departure time
  const [depHour, depMinute] = departureTime?.split(':')?.map(Number);
  const departureDate = new Date(currentTime);
  departureDate?.setHours(depHour, depMinute, 0, 0);

  // Calculate end time
  const endDate = new Date(departureDate?.getTime() + routeDurationMinutes * 60000);

  // Check trip status
  if (currentTime < departureDate) {
    // Trip hasn't started yet - calculate time until departure
    const minutesUntilDeparture = Math.round((departureDate - currentTime) / 60000);
    return {
      etaMinutes: minutesUntilDeparture,
      confidenceInterval: 2,
      status: 'upcoming'
    };
  }

  if (currentTime >= endDate) {
    // Trip has ended - mark as passed so findNearestTrip can skip it
    return {
      etaMinutes: 0,
      confidenceInterval: 0,
      status: 'passed'
    };
  }

  // Trip is active - calculate based on location
  const { distanceToRoute, segmentIndex: userSegmentIndex, progressOnRoute: userProgress } = findNearestPointOnRoute(
    userLocation,
    routePolyline
  );

  // Calculate how far along the route the minibus should be
  const elapsedMinutes = (currentTime - departureDate) / 60000;
  const tripProgress = Math.min(1, elapsedMinutes / routeDurationMinutes);
  const minibusSegmentIndex = Math.floor(tripProgress * (routePolyline?.length - 1));

  // Check if user is ahead of or behind the minibus on the route
  if (userProgress < tripProgress) {
    // User is behind the minibus - minibus has already passed
    return {
      etaMinutes: 0,
      confidenceInterval: 0,
      status: 'passed'
    };
  }

  // Calculate distance from minibus current position to user's position along the route
  let distanceToUser = 0;
  
  // Add distance from minibus position to end of its current segment
  if (minibusSegmentIndex < routePolyline?.length - 1) {
    const minibusSegmentStart = routePolyline?.[minibusSegmentIndex];
    const minibusSegmentEnd = routePolyline?.[minibusSegmentIndex + 1];
    const segmentLength = calculateDistance(
      minibusSegmentStart?.lat,
      minibusSegmentStart?.lng,
      minibusSegmentEnd?.lat,
      minibusSegmentEnd?.lng
    );
    const segmentProgress = (tripProgress * (routePolyline?.length - 1)) - minibusSegmentIndex;
    distanceToUser += segmentLength * (1 - segmentProgress);
  }

  // Add all complete segments between minibus and user
  for (let i = minibusSegmentIndex + 1; i < userSegmentIndex; i++) {
    const p1 = routePolyline?.[i];
    const p2 = routePolyline?.[i + 1];
    distanceToUser += calculateDistance(p1?.lat, p1?.lng, p2?.lat, p2?.lng);
  }

  // Add distance from start of user's segment to user's position
  if (userSegmentIndex < routePolyline?.length - 1 && userSegmentIndex >= minibusSegmentIndex) {
    const userSegmentStart = routePolyline?.[userSegmentIndex];
    const userSegmentEnd = routePolyline?.[userSegmentIndex + 1];
    const segmentLength = calculateDistance(
      userSegmentStart?.lat,
      userSegmentStart?.lng,
      userSegmentEnd?.lat,
      userSegmentEnd?.lng
    );
    const segmentProgress = (userProgress * (routePolyline?.length - 1)) - userSegmentIndex;
    distanceToUser += segmentLength * segmentProgress;
  }

  // Average speed: 30 km/h in normal traffic, 20 km/h in peak hours
  const averageSpeed = isPeakHour(currentTime) ? 20000 / 60 : 30000 / 60; // meters per minute
  
  // Calculate base ETA based on distance along route
  let baseETAMinutes = Math.round(distanceToUser / averageSpeed);

  // If user is far from route (> 500m), add walking time
  if (distanceToRoute > 500) {
    const walkingSpeed = 80; // meters per minute (4.8 km/h)
    const walkingTime = Math.round((distanceToRoute - 500) / walkingSpeed);
    baseETAMinutes += walkingTime;
  }

  // Apply peak hour multiplier
  if (isPeakHour(currentTime)) {
    baseETAMinutes = Math.round(baseETAMinutes * 1.2);
  }

  // Ensure minimum 1 minute
  baseETAMinutes = Math.max(1, baseETAMinutes);

  // Calculate base confidence interval
  let confidenceInterval = 3;
  if (distanceToRoute > 1000) confidenceInterval = 5;
  if (isPeakHour(currentTime)) confidenceInterval += 2;

  // AI Enhancement: Use Gemini to analyze historical patterns and improve accuracy
  let finalETAMinutes = baseETAMinutes;
  let aiPrediction = null;

  if (useAI && routeId) {
    try {
      // Dynamically import services to avoid circular dependencies
      const { historicalDataService } = await import('../services/historicalDataService.js');
      const { analyzeETAWithAI } = await import('../services/geminiService.js');

      // Check for cached prediction first
      const cachedPrediction = await historicalDataService?.getCachedAIPrediction(routeId, departureTime);
      
      if (cachedPrediction) {
        // Use cached prediction
        finalETAMinutes = baseETAMinutes + cachedPrediction?.predictedDelayMinutes;
        confidenceInterval = Math.round(confidenceInterval * (1 - cachedPrediction?.confidenceScore * 0.5));
        aiPrediction = {
          source: 'cache',
          adjustmentMinutes: cachedPrediction?.predictedDelayMinutes,
          confidenceScore: cachedPrediction?.confidenceScore
        };
      } else {
        // Fetch historical data and analyze with AI
        const dayOfWeek = currentTime?.getDay();
        const isPeak = isPeakHour(currentTime);
        
        const [historicalDelays, currentTraffic] = await Promise.all([
          historicalDataService?.getHistoricalDelays(routeId, departureTime, dayOfWeek, isPeak),
          historicalDataService?.getCurrentTrafficConditions(routeId)
        ]);

        if (historicalDelays?.length > 0) {
          // Analyze with Gemini AI
          const prediction = await analyzeETAWithAI({
            routeId,
            departureTime,
            baseETAMinutes,
            isPeakHour: isPeak,
            historicalDelays,
            currentTraffic,
            currentTime
          });

          if (prediction) {
            finalETAMinutes = prediction?.predictedETA;
            confidenceInterval = prediction?.confidenceInterval;
            aiPrediction = {
              source: 'ai',
              adjustmentMinutes: prediction?.adjustmentMinutes,
              confidenceScore: prediction?.confidenceScore,
              reasoning: prediction?.reasoning,
              factors: prediction?.factors
            };

            // Cache the prediction
            await historicalDataService?.saveAIPrediction(routeId, departureTime, prediction);
          }
        }
      }
    } catch (error) {
      // Silently fall back to base calculation on any error (network, API, etc.)
      const errorMessage = error?.message || error?.toString?.() || 'Unknown error';
      console.warn('AI enhancement unavailable, using base calculation:', errorMessage);
      // Ensure we continue with base calculation
      finalETAMinutes = baseETAMinutes;
    }
  }

  return {
    etaMinutes: finalETAMinutes,
    confidenceInterval,
    status: 'active',
    aiPrediction
  };
};

/**
 * Calculate ETA for multiple departure times and return the nearest one
 * @param {Object} params - Calculation parameters
 * @param {Object} params.userLocation - User location
 * @param {Array} params.routePolyline - Route geometry
 * @param {Array} params.departureTimes - Array of departure times
 * @param {number} params.routeDurationMinutes - Route duration
 * @param {Date} params.currentTime - Current time
 * @param {string} params.routeId - Route ID (optional, for AI enhancement)
 * @param {boolean} params.useAI - Whether to use AI enhancement (default: true)
 * @returns {Object} Nearest active or upcoming trip with ETA
 */
export const findNearestTrip = async ({
  userLocation,
  routePolyline,
  departureTimes,
  routeDurationMinutes,
  currentTime = new Date(),
  routeId = null,
  useAI = true
}) => {
  if (!departureTimes || departureTimes?.length === 0) {
    return null;
  }

  const currentHour = currentTime?.getHours();
  const currentMinute = currentTime?.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  const tripPromises = departureTimes?.map(async (departureTime) => {
    const eta = await calculateETAWithLocation({
      userLocation,
      routePolyline,
      departureTime,
      routeDurationMinutes,
      currentTime,
      routeId,
      useAI
    });
    
    // Parse departure time to minutes for comparison
    const [depHour, depMinute] = departureTime?.split(':')?.map(Number);
    const depTimeInMinutes = depHour * 60 + depMinute;
    
    return { 
      departureTime, 
      departureTimeInMinutes: depTimeInMinutes,
      ...eta 
    };
  });

  const trips = await Promise.all(tripPromises);

  // Find first active trip (currently running and hasn't passed user yet)
  const activeTrip = trips?.find(trip => trip?.status === 'active');
  if (activeTrip) return activeTrip;

  // Find next upcoming trip (hasn't started yet)
  const upcomingTrips = trips?.filter(trip => 
    trip?.status === 'upcoming'
  );
  
  if (upcomingTrips?.length > 0) {
    // Sort by departure time and return the earliest
    upcomingTrips?.sort((a, b) => a?.departureTimeInMinutes - b?.departureTimeInMinutes);
    return upcomingTrips?.[0];
  }

  // If no upcoming trips today, return null (no more departures)
  return null;
};
